/**
 * チャットルーム管理TanStack Queryフック
 * UNIFIED_DEVELOPMENT_GUIDE準拠
 */

import { useQuery, useMutation, useQueryClient, UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconX } from '@tabler/icons-react';
import React from 'react';
import { chatRoomApi } from '../api';
import type {
  ChatRoom,
  ChatRoomFormData,
  ChatRoomFilters,
  ChatRoomListResponse,
} from '../types';

// ========== クエリキー管理 ==========

export const chatRoomQueryKeys = {
  all: ['chat', 'rooms'] as const,
  lists: () => [...chatRoomQueryKeys.all, 'list'] as const,
  list: (filters?: ChatRoomFilters) => [...chatRoomQueryKeys.lists(), filters] as const,
  details: () => [...chatRoomQueryKeys.all, 'detail'] as const,
  detail: (id: number) => [...chatRoomQueryKeys.details(), id] as const,
  messages: (id: number) => ['chat', 'messages', id] as const,
  participants: (id: number) => ['chat', 'participants', id] as const,
} as const;

// ========== 一覧取得フック ==========

export interface UseChatRoomListOptions extends Omit<UseQueryOptions<ChatRoomListResponse>, 'queryKey' | 'queryFn'> {
  filters?: ChatRoomFilters;
  enabled?: boolean;
}

/**
 * チャットルーム一覧取得フック
 */
export const useChatRoomList = (options: UseChatRoomListOptions = {}) => {
  const { filters, enabled = true, ...queryOptions } = options;

  return useQuery({
    queryKey: chatRoomQueryKeys.list(filters),
    queryFn: () => chatRoomApi.list(filters),
    enabled,
    staleTime: filters?.search ? 30 * 1000 : 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: (previousData) => previousData,
    ...queryOptions,
  });
};

// ========== 詳細取得フック ==========

export interface UseChatRoomDetailOptions extends Omit<UseQueryOptions<ChatRoom>, 'queryKey' | 'queryFn'> {}

/**
 * チャットルーム詳細取得フック
 */
export const useChatRoomDetail = (id: number, options: UseChatRoomDetailOptions = {}) => {
  return useQuery({
    queryKey: chatRoomQueryKeys.detail(id),
    queryFn: () => chatRoomApi.get(id),
    enabled: !!id && id > 0,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    ...options,
  });
};

// ========== 作成Mutation ==========

export interface UseCreateChatRoomOptions extends UseMutationOptions<ChatRoom, Error, ChatRoomFormData> {
  showNotification?: boolean;
}

/**
 * チャットルーム作成Mutation
 */
export const useCreateChatRoom = (options: UseCreateChatRoomOptions = {}) => {
  const queryClient = useQueryClient();
  const { showNotification = true, ...mutationOptions } = options;

  return useMutation({
    mutationFn: (data: ChatRoomFormData) => chatRoomApi.create(data),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: chatRoomQueryKeys.lists() });

      if (showNotification) {
        notifications.show({
          title: '成功',
          message: 'チャットルームを作成しました',
          color: 'green',
          icon: React.createElement(IconCheck, { size: '1rem' }),
        });
      }

      mutationOptions.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      if (showNotification) {
        notifications.show({
          title: 'エラー',
          message: `チャットルームの作成に失敗しました: ${error.message}`,
          color: 'red',
          icon: React.createElement(IconX, { size: '1rem' }),
        });
      }

      mutationOptions.onError?.(error, variables, context);
    },
    ...mutationOptions,
  });
};

// ========== 更新Mutation ==========

export interface UseUpdateChatRoomOptions extends UseMutationOptions<ChatRoom, Error, { id: number; data: Partial<ChatRoomFormData> }> {
  showNotification?: boolean;
}

/**
 * チャットルーム更新Mutation
 */
export const useUpdateChatRoom = (options: UseUpdateChatRoomOptions = {}) => {
  const queryClient = useQueryClient();
  const { showNotification = true, ...mutationOptions } = options;

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<ChatRoomFormData> }) =>
      chatRoomApi.update(id, data),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: chatRoomQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: chatRoomQueryKeys.detail(variables.id) });

      if (showNotification) {
        notifications.show({
          title: '成功',
          message: 'チャットルームを更新しました',
          color: 'green',
          icon: React.createElement(IconCheck, { size: '1rem' }),
        });
      }

      mutationOptions.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      if (showNotification) {
        notifications.show({
          title: 'エラー',
          message: `チャットルームの更新に失敗しました: ${error.message}`,
          color: 'red',
          icon: React.createElement(IconX, { size: '1rem' }),
        });
      }

      mutationOptions.onError?.(error, variables, context);
    },
    ...mutationOptions,
  });
};

// ========== 削除Mutation ==========

export interface UseDeleteChatRoomOptions extends UseMutationOptions<void, Error, number> {
  showNotification?: boolean;
}

/**
 * チャットルーム削除Mutation
 */
export const useDeleteChatRoom = (options: UseDeleteChatRoomOptions = {}) => {
  const queryClient = useQueryClient();
  const { showNotification = true, ...mutationOptions } = options;

  return useMutation({
    mutationFn: (id: number) => chatRoomApi.delete(id),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: chatRoomQueryKeys.lists() });
      queryClient.removeQueries({ queryKey: chatRoomQueryKeys.detail(variables) });

      if (showNotification) {
        notifications.show({
          title: '成功',
          message: 'チャットルームを削除しました',
          color: 'green',
          icon: React.createElement(IconCheck, { size: '1rem' }),
        });
      }

      mutationOptions.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      if (showNotification) {
        notifications.show({
          title: 'エラー',
          message: `チャットルームの削除に失敗しました: ${error.message}`,
          color: 'red',
          icon: React.createElement(IconX, { size: '1rem' }),
        });
      }

      mutationOptions.onError?.(error, variables, context);
    },
    ...mutationOptions,
  });
};

// ========== 参加Mutation ==========

export interface UseJoinChatRoomOptions extends UseMutationOptions<void, Error, number> {
  showNotification?: boolean;
}

/**
 * チャットルーム参加Mutation
 */
export const useJoinChatRoom = (options: UseJoinChatRoomOptions = {}) => {
  const queryClient = useQueryClient();
  const { showNotification = true, ...mutationOptions } = options;

  return useMutation({
    mutationFn: (id: number) => chatRoomApi.join(id),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: chatRoomQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: chatRoomQueryKeys.detail(variables) });

      if (showNotification) {
        notifications.show({
          title: '成功',
          message: 'チャットルームに参加しました',
          color: 'green',
          icon: React.createElement(IconCheck, { size: '1rem' }),
        });
      }

      mutationOptions.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      if (showNotification) {
        notifications.show({
          title: 'エラー',
          message: `チャットルームへの参加に失敗しました: ${error.message}`,
          color: 'red',
          icon: React.createElement(IconX, { size: '1rem' }),
        });
      }

      mutationOptions.onError?.(error, variables, context);
    },
    ...mutationOptions,
  });
};

// ========== 退出Mutation ==========

export interface UseLeaveChatRoomOptions extends UseMutationOptions<void, Error, number> {
  showNotification?: boolean;
}

/**
 * チャットルーム退出Mutation
 */
export const useLeaveChatRoom = (options: UseLeaveChatRoomOptions = {}) => {
  const queryClient = useQueryClient();
  const { showNotification = true, ...mutationOptions } = options;

  return useMutation({
    mutationFn: (id: number) => chatRoomApi.leave(id),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: chatRoomQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: chatRoomQueryKeys.detail(variables) });

      if (showNotification) {
        notifications.show({
          title: '成功',
          message: 'チャットルームを退出しました',
          color: 'green',
          icon: React.createElement(IconCheck, { size: '1rem' }),
        });
      }

      mutationOptions.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      if (showNotification) {
        notifications.show({
          title: 'エラー',
          message: `チャットルームからの退出に失敗しました: ${error.message}`,
          color: 'red',
          icon: React.createElement(IconX, { size: '1rem' }),
        });
      }

      mutationOptions.onError?.(error, variables, context);
    },
    ...mutationOptions,
  });
};

// ========== 既読Mutation ==========

export interface UseMarkAsReadOptions extends UseMutationOptions<void, Error, number> {
  showNotification?: boolean;
}

/**
 * 既読マークMutation
 */
export const useMarkAsRead = (options: UseMarkAsReadOptions = {}) => {
  const queryClient = useQueryClient();
  const { showNotification = false, ...mutationOptions } = options;

  return useMutation({
    mutationFn: (id: number) => chatRoomApi.markAsRead(id),
    onSuccess: (data, variables, context) => {
      // チャットルーム一覧のキャッシュを無効化（未読数を更新）
      queryClient.invalidateQueries({ queryKey: chatRoomQueryKeys.lists() });
      
      // 詳細のキャッシュも無効化
      queryClient.invalidateQueries({ queryKey: chatRoomQueryKeys.detail(variables) });
      
      // ヘッダー表示用の未読数を即座に更新するため、すべてのchatルーム関連キャッシュを無効化
      queryClient.invalidateQueries({ queryKey: ['chat', 'rooms'] });

      mutationOptions.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      if (showNotification) {
        notifications.show({
          title: 'エラー',
          message: `既読マークの更新に失敗しました: ${error.message}`,
          color: 'red',
          icon: React.createElement(IconX, { size: '1rem' }),
        });
      }

      mutationOptions.onError?.(error, variables, context);
    },
    ...mutationOptions,
  });
};